import { HTMLAttributes, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import * as styles from './conversation-item-view.css';
import { observer } from 'mobx-react-lite';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { LoadingAnimation } from '@instamenu/ui-icons';
import { ChatHistoryItem } from '../types';
import { FileText, Pencil } from 'lucide-react';
import { FunctionCall } from './function-call';
import { Button, ImageCrop } from '@instamenu/ui-system-next';
import { useThreadContext } from '../context/context';
import { ConversationItemActions } from './conversation-item-actions';
import { MessageEdit } from './message-edit';
import { Reasoning, ReasoningContent, ReasoningTrigger } from './reasoning';

type ConversationItemViewProps = {
  item: ChatHistoryItem;
  messageClassName?: typeof styles.message;
  showActions?: boolean;
  onImageChange?: (itemId: string, dataUri: string) => Promise<void> | void;
  isEditing?: boolean;
  onStartEditing?: (messageId: string) => void;
  onCancelEditing?: () => void;
} & HTMLAttributes<HTMLDivElement>;

export const ConversationItemView = observer((props: ConversationItemViewProps) => {
  const {
    item,
    messageClassName,
    showActions = false,
    onImageChange,
    isEditing = false,
    onStartEditing,
    onCancelEditing,
    ...rest
  } = props;
  const { thread } = useThreadContext();
  const [isHovering, setIsHovering] = useState(false);
  const [editingImageSrc, setEditingImageSrc] = useState<string | null>(null);

  if (item.role === 'developer') return null;
  if (item.type === 'reasoning' && item.role === 'assistant') {
    return (
      <Reasoning isStreaming={item.status === 'incomplete'}>
        <ReasoningTrigger />
        <ReasoningContent markdown>{item.content.text}</ReasoningContent>
      </Reasoning>
    );
  }
  if (item.type === 'message') {
    if (item.content.type === 'image' && item.role === 'assistant' && item.status === 'failed')
      return null;

    const isUserMessage = item.role === 'user';
    const role = isUserMessage ? 'user' : 'assistant';

    const isAIGeneratedImage =
      item.content.type === 'image' && item.role === 'assistant' && !item.content.partial;

    const handleCrop = async (dataUri: string) => {
      await onImageChange?.(item.id, dataUri);
      setEditingImageSrc(null);
    };

    return (
      <div className={styles.messageWrapper[role]}>
        <div
          className={clsx(
            styles.messageBase,
            (messageClassName ?? styles.message)({
              role: item.role,
              type: item.content.type,
              editing: isEditing,
            }),
          )}
          style={{ position: 'relative' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          {...rest}
        >
          {/* {item.role === 'assistant' && item.status + ':' + item.responseId} */}
          {item.content.type === 'image' ? (
            <>
              {!editingImageSrc && (
                <>
                  <img
                    src={item.content.url}
                    alt="AI generated"
                    className={styles.image({ role: item.role })}
                  />
                  {item.content.partial && (
                    <LoadingAnimation
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginLeft: -32,
                        marginTop: -32,
                      }}
                      width={64}
                      height={64}
                      color="#FFF"
                    />
                  )}
                  {isAIGeneratedImage && isHovering && onImageChange && (
                    <Button
                      size="xs"
                      onClick={() => {
                        if (item.content.type === 'image' && item.content.url) {
                          setEditingImageSrc(item.content.url);
                        }
                      }}
                      variant="secondary"
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                      }}
                    >
                      <Pencil size={18} />
                    </Button>
                  )}
                </>
              )}
              {editingImageSrc && (
                <ImageCrop
                  imageSrc={editingImageSrc}
                  setImageSrc={(src) => {
                    if (src === null || src === '') {
                      setEditingImageSrc(null);
                    } else {
                      setEditingImageSrc(src);
                    }
                  }}
                  showAspectRatioSelector={true}
                  onCrop={handleCrop}
                />
              )}
            </>
          ) : item.content.type === 'text' ? (
            isEditing ? (
              <MessageEdit item={item} thread={thread} onClose={() => onCancelEditing?.()} />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node, ...rest }) => (
                    <div className={styles.tableWrap}>
                      <table {...rest} />
                    </div>
                  ),
                  img({ node, ...rest }) {
                    return <img className={styles.markdownImg} {...rest} />;
                  },
                }}
              >
                {item.content.text}
              </ReactMarkdown>
            )
          ) : item.content.type === 'file' ? (
            <a
              className={styles.fileAttachment}
              href={item.content.url}
              download={item.content.name}
              target="_blank"
              rel="noreferrer"
            >
              <FileText size={20} />
              <div>
                <div style={{ fontWeight: 500 }}>{item.content.name}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{item.content.mimeType}</div>
              </div>
            </a>
          ) : null}
        </div>
        {showActions && !isEditing && item.content.type === 'text' && (
          <div className={styles.actionsContainer[role]}>
            <ConversationItemActions item={item} onStartEditing={onStartEditing} />
          </div>
        )}
      </div>
    );
  }
  if (item.type === 'function_call') {
    return <FunctionCall {...rest} item={item} defaultOpen={false} />;
  }
  /*
  // useful for debugging
  if (props.item.type === 'function_call_output') return null;
  */
  return null;
});
